import { PAGE_LIMIT_MAX, errorWords } from "@alm/domain";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { toast } from "sonner";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { DataTable } from "@/components/app/patterns/data-table";
import { DataTableToolbar } from "@/components/app/patterns/data-table-toolbar";
import { PersonCell } from "@/components/app/patterns/person-cell";
import { residentNameLink } from "@/components/app/lists/record-link";
import { RowActions } from "@/components/app/patterns/row-actions";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { RemoveConfirm } from "@/components/app/fields/remove-confirm";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useFacility } from "@/lib/facility/facility";
import { Plus, Trash2 } from "@/lib/icons";
import { orpc, type orpcClient, type Output } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { activityTypeClass, capitalize } from "./-components/activity-types";
import { LogActivityDialog } from "./-components/log-activity-dialog";

export const Route = createFileRoute("/_authenticated/_care/activities/")({
  component: ActivitiesPage,
});

type Row = Output<typeof orpcClient.care.activities.list>["rows"][number];

const performedLabel = (value: Date | string) =>
  new Date(value).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

const COLUMNS: ColumnDef<Row>[] = [
  {
    id: "resident",
    header: "Resident",
    accessorFn: (row) => `${row.residentFirstName} ${row.residentLastName}`,
    enableHiding: false,
    meta: { filter: "select", className: "max-w-48" },
    cell: ({ getValue, row: { original: row } }) => (
      <PersonCell
        kind="resident"
        name={getValue<string>()}
        renderLink={residentNameLink(row.residentId)}
      />
    ),
  },
  {
    id: "type",
    header: "Type",
    accessorFn: (row) => capitalize(row.activityType),
    meta: { filter: "select", nowrap: true },
    cell: ({ row: { original: row } }) => (
      <Badge variant="outline" className={activityTypeClass(row.activityType)}>
        {capitalize(row.activityType)}
      </Badge>
    ),
  },
  {
    id: "description",
    header: "Description",
    accessorKey: "description",
    meta: { clamp: "lg" },
  },
  {
    id: "performedAt",
    header: "Performed at",
    accessorFn: (row) => new Date(row.performedAt).getTime(),
    enableGlobalFilter: false,
    meta: { nowrap: true, className: "text-muted-foreground" },
    cell: ({ row: { original: row } }) => performedLabel(row.performedAt),
  },
];

function ActivitiesPage() {
  const { facilityId } = useFacility();
  const canManage = useCan("activity:manage");
  const queryClient = useQueryClient();
  const [logging, setLogging] = useState(false);
  const [removing, setRemoving] = useState<{ id: string; name: string } | null>(null);

  const list = useQuery(
    orpc.care.activities.list.queryOptions({
      input: { facilityId, limit: PAGE_LIMIT_MAX, offset: 0 },
    }),
  );
  const residents = useQuery(
    orpc.care.residents.list.queryOptions({
      input: { facilityId, status: "active", limit: PAGE_LIMIT_MAX, offset: 0 },
    }),
  );
  const remove = useMutation(
    orpc.care.activities.remove.mutationOptions({
      onSuccess: async () => {
        toast.success("Activity deleted");
        await queryClient.invalidateQueries({ queryKey: orpc.care.activities.key() });
      },
      onError: (error) =>
        toast.error(errorWords(error, "Couldn't delete the activity. Try again in a moment.")),
    }),
  );

  const all = list.data?.rows ?? [];
  const people = residents.data?.rows ?? [];
  const log = canManage ? () => setLogging(true) : undefined;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Activity log"
        count={list.isSuccess ? all.length : undefined}
        meta={list.isPending ? "Loading…" : undefined}
        actions={
          log ? (
            <Button onClick={log}>
              <Plus className="size-4" />
              Log activity
            </Button>
          ) : null
        }
      />

      {list.isError ? (
        <Card className="gap-0 py-0">
          <ReadFailed
            className="p-4"
            message="Could not load the activity log."
            error={list.error}
            onRetry={() => void list.refetch()}
            retrying={list.isFetching}
          />
        </Card>
      ) : (
        <DataTable
          virtual
          data={all}
          columns={COLUMNS}
          noun="activities"
          rowId={(row) => row.id}
          pending={list.isPending}
          renderToolbar={(table) => (
            <DataTableToolbar table={table} searchPlaceholder="Search activities…" />
          )}
          renderRowActions={
            canManage
              ? (row) => (
                  <RowActions
                    label={`${row.residentFirstName} ${row.residentLastName}'s activity`}
                    modal={false}
                    items={[
                      {
                        label: "Delete activity",
                        icon: Trash2,
                        destructive: true,
                        onSelect: () =>
                          setRemoving({
                            id: row.id,
                            name: `${row.residentFirstName} ${row.residentLastName}`,
                          }),
                      },
                    ]}
                  />
                )
              : undefined
          }
          empty={
            <EmptyState
              title="No activities logged"
              description="Meals, exercise, social time and therapy appear here once staff record them."
            />
          }
        />
      )}

      {logging ? <LogActivityDialog residents={people} onClose={() => setLogging(false)} /> : null}
      {removing ? (
        <RemoveConfirm
          title={`Delete the activity for ${removing.name}?`}
          description="Deleting it removes it from the activity log permanently."
          confirmLabel="Delete activity"
          onConfirm={() => remove.mutate({ activityId: removing.id })}
          onClose={() => setRemoving(null)}
        />
      ) : null}
    </div>
  );
}
